import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { X, Download, FileText, FileSpreadsheet, FileImage, File as FileIcon, Loader2 } from 'lucide-react';
import * as XLSX from 'xlsx';
import axiosInstance from '../api/axios';

function DocumentPreviewModal({ isOpen, onClose, fileId, fileName }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [fileType, setFileType] = useState(null);

  // PDF — native browser rendering via blob URL
  const [pdfBlobUrl, setPdfBlobUrl] = useState(null);

  // Excel
  const [excelHtml, setExcelHtml] = useState('');
  const [sheetNames, setSheetNames] = useState([]);
  const [activeSheet, setActiveSheet] = useState('');
  const [workbookRef, setWorkbookRef] = useState(null);

  // Image
  const [imageUrl, setImageUrl] = useState(null);

  // Download
  const [downloadBlobUrl, setDownloadBlobUrl] = useState(null);

  const ext = fileName?.split('.').pop()?.toLowerCase();

  const headerIcon = useMemo(() => {
    if (['xlsx', 'xls', 'csv'].includes(ext)) return { Icon: FileSpreadsheet, color: 'text-green-600' };
    if (['pdf'].includes(ext)) return { Icon: FileText, color: 'text-red-600' };
    if (['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg'].includes(ext)) return { Icon: FileImage, color: 'text-purple-600' };
    return { Icon: FileIcon, color: 'text-gray-400' };
  }, [ext]);

  const mimeTypes = {
    xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    xls: 'application/vnd.ms-excel',
    csv: 'text/csv',
    pdf: 'application/pdf',
    png: 'image/png',
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    gif: 'image/gif',
    webp: 'image/webp',
    svg: 'image/svg+xml',
    doc: 'application/msword',
    docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  };

  const fetchFile = useCallback(async () => {
    if (!fileId) return;
    setLoading(true);
    setError(null);
    setPdfBlobUrl(null);
    setExcelHtml('');
    setImageUrl(null);
    setDownloadBlobUrl(null);

    try {
      const response = await axiosInstance.get(`/api/control-upload-content/${fileId}`, {
        responseType: 'arraybuffer'
      });

      const data = response.data;
      const mime = mimeTypes[ext] || 'application/octet-stream';
      const blob = new Blob([data], { type: mime });
      const blobUrl = URL.createObjectURL(blob);
      setDownloadBlobUrl(blobUrl);

      if (['pdf'].includes(ext)) {
        setFileType('pdf');
        setPdfBlobUrl(blobUrl);
      } else if (['xlsx', 'xls', 'csv'].includes(ext)) {
        setFileType('excel');
        const wb = XLSX.read(new Uint8Array(data), { type: 'array', cellStyles: true });
        setWorkbookRef(wb);
        setSheetNames(wb.SheetNames);
        const firstSheet = wb.SheetNames[0];
        setActiveSheet(firstSheet);
        setExcelHtml(XLSX.utils.sheet_to_html(wb.Sheets[firstSheet], { editable: false }));
      } else if (['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg'].includes(ext)) {
        setFileType('image');
        setImageUrl(blobUrl);
      } else {
        setFileType('unsupported');
      }
    } catch (err) {
      console.error('Failed to load file:', err);
      setError('Failed to load file for preview');
    }
    setLoading(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fileId, ext]);

  useEffect(() => {
    if (isOpen) {
      fetchFile();
    }
    return () => {
      if (pdfBlobUrl) URL.revokeObjectURL(pdfBlobUrl);
      if (imageUrl) URL.revokeObjectURL(imageUrl);
      if (downloadBlobUrl && downloadBlobUrl !== pdfBlobUrl && downloadBlobUrl !== imageUrl) {
        URL.revokeObjectURL(downloadBlobUrl);
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, fetchFile]);

  const switchSheet = (name) => {
    if (!workbookRef) return;
    setActiveSheet(name);
    setExcelHtml(XLSX.utils.sheet_to_html(workbookRef.Sheets[name], { editable: false }));
  };

  const handleDownload = () => {
    const baseUrl = axiosInstance.defaults.baseURL || '';
    window.open(`${baseUrl}/api/control-upload-download/${fileId}`, '_blank');
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-5xl max-h-[90vh] mx-4 flex flex-col" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="px-6 py-3 border-b border-gray-200 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            <headerIcon.Icon className={`h-5 w-5 ${headerIcon.color} flex-shrink-0`} />
            <h3 className="font-semibold text-gray-900 truncate">{fileName}</h3>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <button onClick={handleDownload} disabled={!downloadBlobUrl}
              className="px-3 py-1.5 text-sm text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 inline-flex items-center gap-1 disabled:opacity-40">
              <Download className="h-4 w-4" />
              Download
            </button>
            <button onClick={onClose} className="p-1.5 text-gray-400 hover:text-gray-600 rounded-md hover:bg-gray-100">
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Excel sheet tabs */}
        {fileType === 'excel' && sheetNames.length > 1 && (
          <div className="px-6 py-0 border-b border-gray-200 bg-gray-50 flex gap-0 overflow-x-auto flex-shrink-0">
            {sheetNames.map((name) => (
              <button key={name} onClick={() => switchSheet(name)}
                className={`px-4 py-2 text-xs whitespace-nowrap border-b-2 transition-colors ${
                  activeSheet === name ? 'border-blue-600 text-blue-700 font-medium bg-white' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}>
                {name}
              </button>
            ))}
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-auto" style={{ minHeight: '400px' }}>
          {loading && (
            <div className="flex flex-col items-center justify-center h-64">
              <Loader2 className="h-8 w-8 text-blue-600 animate-spin mb-3" />
              <p className="text-gray-500">Loading preview...</p>
            </div>
          )}

          {error && (
            <div className="flex flex-col items-center justify-center h-64">
              <p className="text-red-500 mb-2">{error}</p>
              <button onClick={handleDownload} className="text-blue-600 hover:text-blue-700 text-sm">Download file instead</button>
            </div>
          )}

          {/* PDF — native browser rendering */}
          {!loading && !error && fileType === 'pdf' && pdfBlobUrl && (
            <iframe
              src={pdfBlobUrl}
              title={fileName}
              className="w-full h-full border-0"
              style={{ minHeight: '75vh' }}
            />
          )}

          {/* Excel */}
          {!loading && !error && fileType === 'excel' && excelHtml && (
            <div className="p-4 overflow-auto excel-preview"
              dangerouslySetInnerHTML={{ __html: excelHtml }}
              style={{ fontSize: '13px' }}
            />
          )}
          <style>{`
            .excel-preview table { border-collapse: collapse; width: 100%; }
            .excel-preview td, .excel-preview th { border: 1px solid #e5e7eb; padding: 6px 10px; text-align: left; }
            .excel-preview tr:first-child td, .excel-preview th { background: #f9fafb; font-weight: 600; color: #374151; white-space: nowrap; }
            .excel-preview tr:hover td { background: #f3f4f6; }
            .excel-preview td { max-width: 500px; word-wrap: break-word; overflow-wrap: break-word; }
          `}</style>

          {/* Image */}
          {!loading && !error && fileType === 'image' && imageUrl && (
            <div className="flex justify-center p-4">
              <img src={imageUrl} alt={fileName} className="max-w-full max-h-[70vh] object-contain" />
            </div>
          )}

          {/* Unsupported */}
          {!loading && !error && fileType === 'unsupported' && (
            <div className="flex flex-col items-center justify-center h-64">
              <FileText className="h-12 w-12 text-gray-300 mb-3" />
              <p className="text-gray-500 mb-2">Preview not available for .{ext} files</p>
              <button onClick={handleDownload} className="text-blue-600 hover:text-blue-700 text-sm">Download to view</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default DocumentPreviewModal;
